import { useId, useState, type ReactNode } from "react";
import { Pencil } from "lucide-react";
import { useI18n } from "../lib/i18n";

export type PostingReviewField = { id: string; label: string; value: string; editor: ReactNode };

// [posting.validation-and-review](../../../docs/product/marketplace.md#posting-validation-and-review): all editors update their owning wizard's controlled draft. The
// shared view only expands/collapses rows; it never saves or changes steps.
export function PostingReview({ fields, disabled = false }: { fields: PostingReviewField[]; disabled?: boolean }) {
  const { t } = useI18n();
  const instanceId = useId();
  const [editing, setEditing] = useState<string | null>(null);
  return <div className="text-sm">
    {fields.map(({ id, label, value, editor }) => {
      const expanded = editing === id;
      const editorId = `${instanceId}-${id}-editor`;
      return <div key={id} className="border-b border-line py-3">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1 sm:grid sm:grid-cols-[minmax(0,0.7fr)_minmax(0,1.3fr)] sm:gap-3">
            <span className="text-sm text-mut">{label}</span>
            {!expanded && <p className="mt-1 sm:mt-0 whitespace-pre-line break-words font-medium text-navy">{value || "—"}</p>}
          </div>
          <button type="button" disabled={disabled} aria-label={`${expanded ? "Done editing" : "Edit"} ${label}`} aria-expanded={expanded} aria-controls={expanded ? editorId : undefined} onClick={() => setEditing(expanded ? null : id)} className="inline-flex shrink-0 items-center gap-1 rounded px-2 py-1 text-xs font-semibold text-brand-red hover:bg-brand-red/5 disabled:opacity-50">
            {!expanded && <Pencil className="h-3 w-3" aria-hidden="true" />}{expanded ? t("common.done", "Done") : t("common.edit", "Edit")}
          </button>
        </div>
        {expanded && <fieldset id={editorId} disabled={disabled} className="mt-2 min-w-0">{editor}</fieldset>}
      </div>;
    })}
  </div>;
}
